/**
 * CreateDeviceGroupModal - Modal for creating a new device control group
 * Allows user to select devices of the same type within a zone
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { X, Check, AlertCircle } from 'lucide-react';
import LayersIcon from '@mui/icons-material/Layers';
import { DeviceZone, createDeviceControlGroup } from '../services/userPreferencesApi';

// Device type icons
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import BlindsIcon from '@mui/icons-material/Blinds';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import PowerIcon from '@mui/icons-material/Power';
import AirIcon from '@mui/icons-material/Air';

interface Device {
  deviceId: string;
  baseSN: string;
  deviceType: string;
  displayName?: string;
  name?: string;
  nickname?: string;
  deviceName?: string;
  isOn?: boolean;
  online?: boolean;
}

interface DeviceControlGroup {
  id: string;
  devices: string[];
}

interface CreateDeviceGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
  zones: DeviceZone[];
  devices: Device[];
  existingGroups?: DeviceControlGroup[];  // Existing groups to filter out already grouped devices
  preSelectedZoneId?: string;
  preSelectedDevices?: string[];
}

// Get device type icon
const getDeviceTypeIcon = (deviceType: string) => {
  const iconClass = 'w-5 h-5';
  
  switch (deviceType) {
    case 'light':
      return <LightbulbIcon className={iconClass} />;
    case 'hvac':
    case 'ac':
      return <AcUnitIcon className={iconClass} />;
    case 'curtain':
    case 'blind':
      return <BlindsIcon className={iconClass} />;
    case 'switch':
      return <ToggleOnIcon className={iconClass} />;
    case 'outlet':
      return <PowerIcon className={iconClass} />;
    case 'fan':
      return <AirIcon className={iconClass} />;
    default:
      return <LayersIcon className={iconClass} />;
  }
};

// Get device type display name
const getDeviceTypeName = (deviceType: string): string => {
  const names: Record<string, string> = {
    light: '燈光',
    hvac: '空調',
    ac: '冷氣',
    curtain: '窗簾',
    blind: '捲簾',
    switch: '開關',
    outlet: '插座',
    fan: '風扇'
  };
  return names[deviceType] || deviceType;
};

// Supported device types for grouping
const SUPPORTED_DEVICE_TYPES = ['light', 'hvac', 'ac', 'curtain', 'blind', 'switch', 'outlet', 'fan'];

const CreateDeviceGroupModal: React.FC<CreateDeviceGroupModalProps> = ({
  isOpen,
  onClose,
  onCreated,
  zones,
  devices,
  existingGroups = [],
  preSelectedZoneId,
  preSelectedDevices = []
}) => {
  const [groupName, setGroupName] = useState('');
  const [selectedZoneId, setSelectedZoneId] = useState(preSelectedZoneId || '');
  const [selectedDeviceType, setSelectedDeviceType] = useState('');
  const [selectedDevices, setSelectedDevices] = useState<string[]>(preSelectedDevices);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync selectedZoneId when preSelectedZoneId changes (e.g., switching zones)
  useEffect(() => {
    if (preSelectedZoneId) {
      setSelectedZoneId(preSelectedZoneId);
      // Reset device type and selected devices when zone changes
      setSelectedDeviceType('');
      setSelectedDevices([]);
    }
  }, [preSelectedZoneId]);

  // Get devices in selected zone
  const zoneDevices = useMemo(() => {
    if (!selectedZoneId) return [];
    
    const zone = zones.find(z => z.id === selectedZoneId);
    if (!zone) return [];

    return devices.filter(d => 
      zone.devices.includes(d.deviceId) || zone.devices.includes(d.baseSN)
    );
  }, [selectedZoneId, zones, devices]);

  // Get available device types in selected zone
  const availableDeviceTypes = useMemo(() => {
    const types = new Set<string>();
    zoneDevices.forEach(d => {
      if (SUPPORTED_DEVICE_TYPES.includes(d.deviceType)) {
        types.add(d.deviceType);
      }
    });
    return Array.from(types);
  }, [zoneDevices]);

  // Get devices of selected type
  // Get all device IDs that are already in existing groups
  const groupedDeviceIds = useMemo(() => {
    const ids = new Set<string>();
    existingGroups.forEach(group => {
      group.devices.forEach(deviceId => ids.add(deviceId));
    });
    return ids;
  }, [existingGroups]);

  const filteredDevices = useMemo(() => {
    if (!selectedDeviceType) return [];
    return zoneDevices.filter(d => 
      d.deviceType === selectedDeviceType && !groupedDeviceIds.has(d.deviceId)
    );
  }, [zoneDevices, selectedDeviceType, groupedDeviceIds]);

  // Handle zone change
  const handleZoneChange = useCallback((zoneId: string) => {
    setSelectedZoneId(zoneId);
    setSelectedDeviceType('');
    setSelectedDevices([]);
  }, []);

  // Handle device type change
  const handleDeviceTypeChange = useCallback((type: string) => {
    setSelectedDeviceType(type);
    setSelectedDevices([]);
  }, []);

  // Toggle device selection
  const toggleDevice = useCallback((deviceId: string) => {
    setSelectedDevices(prev => 
      prev.includes(deviceId)
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  }, []);

  // Select/deselect all
  const toggleSelectAll = useCallback(() => {
    if (selectedDevices.length === filteredDevices.length) {
      setSelectedDevices([]);
    } else {
      setSelectedDevices(filteredDevices.map(d => d.deviceId));
    }
  }, [selectedDevices, filteredDevices]);

  // Get display name for device
  const getDisplayName = (device: Device): string => {
    return device.nickname || device.displayName || device.name || device.deviceName || device.deviceId;
  };

  // Create group
  const handleCreate = async () => {
    if (!groupName.trim()) {
      setError('請輸入群組名稱');
      return;
    }
    if (selectedDevices.length < 2) {
      setError('請選擇至少 2 個設備');
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      await createDeviceControlGroup(
        groupName.trim(),
        selectedZoneId,
        selectedDeviceType,
        selectedDevices
      );
      onCreated();
      // Reset state and close
      setGroupName('');
      setSelectedZoneId(preSelectedZoneId || '');
      setSelectedDeviceType('');
      setSelectedDevices(preSelectedDevices);
      setError(null);
      onClose();
    } catch (err) {
      setError('創建群組失敗，請稍後重試');
      console.error('Failed to create device group:', err);
    } finally {
      setIsCreating(false);
    }
  };

  // Reset state when modal closes
  const handleClose = () => {
    setGroupName('');
    setSelectedZoneId(preSelectedZoneId || '');
    setSelectedDeviceType('');
    setSelectedDevices(preSelectedDevices);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-[#1a2744] rounded-2xl w-full max-w-3xl h-[720px] flex flex-col shadow-2xl border border-slate-700/50">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <LayersIcon className="w-5 h-5 text-blue-400" />
            </div>
            <h2 className="text-lg font-semibold text-white">創建群組</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Content - Two Column Layout */}
        <div className="flex-1 overflow-hidden p-4 flex gap-6">
          {/* Left Column: Group Name & Device Type */}
          <div className="w-1/2 space-y-4 flex flex-col">
            {/* Group Name */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                群組名稱
              </label>
              <input
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="例如：客廳燈控"
                className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Zone Selection - hidden when preSelectedZoneId is provided */}
            {!preSelectedZoneId && (
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  選擇區域
                </label>
                <select
                  value={selectedZoneId}
                  onChange={(e) => handleZoneChange(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">請選擇區域</option>
                  {zones.map(zone => (
                    <option key={zone.id} value={zone.id}>
                      {zone.name} ({zone.devices.length} 設備)
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Device Type Selection */}
            {selectedZoneId && (
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  設備類型
                </label>
                {availableDeviceTypes.length === 0 ? (
                  <p className="text-sm text-slate-500">此區域沒有可群組的設備</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {availableDeviceTypes.map(type => (
                      <button
                        key={type}
                        onClick={() => handleDeviceTypeChange(type)}
                        className={`
                          flex items-center gap-2 px-3 py-2 rounded-lg transition-colors
                          ${selectedDeviceType === type
                            ? 'bg-blue-500/30 border-blue-500 text-blue-300'
                            : 'bg-slate-800/50 border-slate-600/50 text-slate-300 hover:bg-slate-700/50'}
                          border
                        `}
                      >
                        {getDeviceTypeIcon(type)}
                        <span className="text-sm">{getDeviceTypeName(type)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                <span className="text-sm text-red-300">{error}</span>
              </div>
            )}
          </div>

          {/* Right Column: Device Selection */}
          <div className="w-1/2 flex flex-col border-l border-slate-700/50 pl-6">
            {selectedDeviceType && filteredDevices.length > 0 ? (
              <>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-slate-300">
                    選擇設備 ({selectedDevices.length}/{filteredDevices.length})
                  </label>
                  <button
                    onClick={toggleSelectAll}
                    className="text-xs text-blue-400 hover:text-blue-300"
                  >
                    {selectedDevices.length === filteredDevices.length ? '取消全選' : '全選'}
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto space-y-2">
                  {filteredDevices.map(device => (
                    <label
                      key={device.deviceId}
                      className={`
                        flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors
                        ${selectedDevices.includes(device.deviceId)
                          ? 'bg-blue-500/20 border-blue-500/50'
                          : 'bg-slate-800/30 border-slate-600/30 hover:bg-slate-700/30'}
                        border
                      `}
                    >
                      <input
                        type="checkbox"
                        checked={selectedDevices.includes(device.deviceId)}
                        onChange={() => toggleDevice(device.deviceId)}
                        className="w-4 h-4 rounded border-slate-500 bg-slate-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                      />
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {getDeviceTypeIcon(device.deviceType)}
                        <span className="text-sm text-white truncate">
                          {getDisplayName(device)}
                        </span>
                      </div>
                      {device.online === false && (
                        <span className="text-xs text-orange-400">離線</span>
                      )}
                    </label>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-sm text-slate-500">
                  {!selectedZoneId ? '請先選擇區域' : !selectedDeviceType ? '請選擇設備類型' : '沒有可用設備'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-700/50">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleCreate}
            disabled={isCreating || !groupName.trim() || selectedDevices.length < 2}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors
              ${isCreating || !groupName.trim() || selectedDevices.length < 2
                ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'}
            `}
          >
            {isCreating ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                創建中...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                創建群組
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateDeviceGroupModal;
